import { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import api, { errDetail } from "../lib/api";
import { PageHeader } from "../components/CrudPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

dayjs.locale("tr");

const TYPE_STYLES = { etkinlik: "#0B1F3A", gorev: "#D4AF37", randevu: "#2563EB" };
const TYPE_LABELS = { etkinlik: "Etkinlik", gorev: "Görev", randevu: "Randevu" };
const TYPE_ENDPOINTS = { etkinlik: "/calendar-events", gorev: "/tasks", randevu: "/appointments" };
const STATUS_LABELS = { bekliyor: "Bekliyor", tamamlandi: "Tamamlandı", iptal: "İptal Edildi" };
const PRIORITY_LABELS = { dusuk: "Düşük", orta: "Orta", yuksek: "Yüksek" };

export default function CalendarPage() {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(dayjs());
  const [items, setItems] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ item_type: "etkinlik", title: "", date: "", time: "09:00", name: "", phone: "", address: "", description: "" });
  const [saving, setSaving] = useState(false);

  const range = view === "month"
    ? [cursor.startOf("month").format("YYYY-MM-DD"), cursor.endOf("month").format("YYYY-MM-DD")]
    : view === "week"
      ? [cursor.startOf("week").format("YYYY-MM-DD"), cursor.endOf("week").format("YYYY-MM-DD")]
      : [cursor.format("YYYY-MM-DD"), cursor.format("YYYY-MM-DD")];

  const load = useCallback(() => {
    api.get("/calendar/all", { params: { start: range[0], end: range[1] } })
      .then((r) => setItems(r.data.items)).catch((e) => toast.error(errDetail(e)));
  }, [range[0], range[1]]);

  useEffect(() => { load(); }, [load]);

  const openCreate = (date) => {
    setForm({ item_type: "etkinlik", title: "", date: date || cursor.format("YYYY-MM-DD"), time: "09:00", name: "", phone: "", address: "", description: "" });
    setCreateOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const t = form.item_type;
      let payload;
      if (t === "randevu") payload = { name: form.name || form.title, phone: form.phone, address: form.address, date: form.date, time: form.time, status: "bekliyor", description: form.description };
      else if (t === "gorev") payload = { title: form.title, date: form.date, time: form.time, priority: "orta", status: "bekliyor", color: "#D4AF37", note: form.description };
      else payload = { title: form.title, date: form.date, time: form.time, color: "#0B1F3A", description: form.description };
      if ((t === "randevu" && !payload.name) || (t !== "randevu" && !payload.title)) {
        toast.error("Başlık zorunludur"); setSaving(false); return;
      }
      await api.post(TYPE_ENDPOINTS[t], payload);
      toast.success(`${TYPE_LABELS[t]} oluşturuldu`);
      setCreateOpen(false);
      load();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`${TYPE_ENDPOINTS[detail.item_type]}/${detail.id}`);
      toast.success("Kayıt silindi");
      setDetail(null);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const itemsFor = (date) => items.filter((i) => i.date === date).sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  const ItemChip = ({ item }) => (
    <button
      data-testid={`calendar-item-${item.id}`}
      onClick={(e) => { e.stopPropagation(); setDetail(item); }}
      className="block w-full truncate px-1.5 py-0.5 text-left text-[11px] font-medium text-white transition-opacity duration-200 hover:opacity-80"
      style={{ backgroundColor: item.color || TYPE_STYLES[item.item_type] }}
    >
      {item.time && `${item.time} `}{item.title}
    </button>
  );

  const renderMonth = () => {
    const start = cursor.startOf("month").startOf("week");
    const days = Array.from({ length: 42 }, (_, i) => start.add(i, "day"));
    return (
      <div className="border border-gray-200 bg-white">
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"].map((d) => (
            <div key={d} className="px-2 py-2 text-center text-xs font-semibold uppercase text-gray-500">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d) => {
            const ds = d.format("YYYY-MM-DD");
            const inMonth = d.month() === cursor.month();
            const isToday = ds === dayjs().format("YYYY-MM-DD");
            return (
              <div key={ds} onClick={() => openCreate(ds)} data-testid={`calendar-day-${ds}`}
                className={`min-h-24 cursor-pointer border-b border-r border-gray-100 p-1.5 transition-colors duration-200 hover:bg-gray-50 ${inMonth ? "" : "bg-gray-50/60 text-gray-300"}`}>
                <span className={`inline-flex h-6 w-6 items-center justify-center text-xs font-medium ${isToday ? "bg-[#D4AF37] font-bold text-[#0B1F3A]" : ""}`}>
                  {d.date()}
                </span>
                <div className="mt-1 space-y-0.5">
                  {itemsFor(ds).slice(0, 3).map((i) => <ItemChip key={i.id} item={i} />)}
                  {itemsFor(ds).length > 3 && <p className="text-[10px] text-gray-400">+{itemsFor(ds).length - 3} daha</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeek = () => {
    const start = cursor.startOf("week");
    const days = Array.from({ length: 7 }, (_, i) => start.add(i, "day"));
    return (
      <div className="grid grid-cols-1 gap-px border border-gray-200 bg-gray-200 md:grid-cols-7">
        {days.map((d) => {
          const ds = d.format("YYYY-MM-DD");
          return (
            <div key={ds} className="min-h-48 cursor-pointer bg-white p-2 transition-colors duration-200 hover:bg-gray-50" onClick={() => openCreate(ds)}>
              <p className="mb-2 text-xs font-semibold uppercase text-gray-500">{d.format("ddd D MMM")}</p>
              <div className="space-y-1">{itemsFor(ds).map((i) => <ItemChip key={i.id} item={i} />)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDay = () => {
    const ds = cursor.format("YYYY-MM-DD");
    const hours = Array.from({ length: 15 }, (_, i) => i + 8);
    return (
      <div className="border border-gray-200 bg-white">
        {hours.map((h) => {
          const hs = String(h).padStart(2, "0");
          const hourItems = itemsFor(ds).filter((i) => (i.time || "").startsWith(hs));
          return (
            <div key={h} className="flex border-b border-gray-100">
              <div className="w-16 shrink-0 border-r border-gray-100 px-2 py-3 text-xs text-gray-400">{hs}:00</div>
              <div className="flex-1 space-y-1 p-2">
                {hourItems.map((i) => <ItemChip key={i.id} item={i} />)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const title = view === "month" ? cursor.format("MMMM YYYY")
    : view === "week" ? `${cursor.startOf("week").format("D MMM")} - ${cursor.endOf("week").format("D MMM YYYY")}`
    : cursor.format("D MMMM YYYY dddd");

  return (
    <div data-testid="calendar-page">
      <PageHeader title="Takvim" subtitle="Etkinlik, görev ve randevularınız tek ekranda">
        <Button data-testid="calendar-add-button" onClick={() => openCreate()}
          className="rounded-none bg-[#D4AF37] font-semibold text-[#0B1F3A] hover:bg-[#c6a230]">
          <Plus size={18} className="mr-1" /> Yeni Oluştur
        </Button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev" onClick={() => setCursor(cursor.subtract(1, view))}
            className="flex h-9 w-9 items-center justify-center border border-gray-200 hover:border-[#D4AF37]"><ChevronLeft size={16} /></button>
          <button data-testid="calendar-today" onClick={() => setCursor(dayjs())}
            className="border border-gray-200 px-4 py-2 text-sm hover:border-[#D4AF37]">Bugün</button>
          <button data-testid="calendar-next" onClick={() => setCursor(cursor.add(1, view))}
            className="flex h-9 w-9 items-center justify-center border border-gray-200 hover:border-[#D4AF37]"><ChevronRight size={16} /></button>
          <h2 className="ml-2 font-heading text-lg font-semibold capitalize">{title}</h2>
        </div>
        <div className="flex border border-gray-200">
          {[["month", "Ay"], ["week", "Hafta"], ["day", "Gün"]].map(([v, l]) => (
            <button key={v} data-testid={`calendar-view-${v}`} onClick={() => setView(v)}
              className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${view === v ? "bg-[#0B1F3A] text-white" : "text-gray-600 hover:bg-gray-50"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {view === "month" && renderMonth()}
      {view === "week" && renderWeek()}
      {view === "day" && renderDay()}

      <div className="mt-4 flex gap-5 text-xs text-gray-500">
        {Object.entries(TYPE_LABELS).map(([k, l]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className="h-3 w-3" style={{ backgroundColor: TYPE_STYLES[k] }} /> {l}
          </span>
        ))}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Yeni Oluştur</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Tür</Label>
              <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                <SelectTrigger data-testid="calendar-create-type" className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.item_type === "randevu" ? (
              <>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Ad Soyad *</Label>
                  <Input data-testid="calendar-create-name" className="rounded-none" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Telefon</Label>
                  <Input className="rounded-none" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Adres</Label>
                  <Input className="rounded-none" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
              </>
            ) : (
              <div>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Başlık *</Label>
                <Input data-testid="calendar-create-title" className="rounded-none" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Tarih</Label>
                <Input data-testid="calendar-create-date" type="date" className="rounded-none" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Saat</Label>
                <Input data-testid="calendar-create-time" type="time" className="rounded-none" value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Açıklama</Label>
              <Textarea data-testid="calendar-create-description" className="rounded-none" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" className="rounded-none" onClick={() => setCreateOpen(false)}>Vazgeç</Button>
              <Button data-testid="calendar-create-save" disabled={saving} onClick={save}
                className="rounded-none bg-[#0B1F3A] text-white hover:bg-[#132c4f]">Oluştur</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(detail)} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-none sm:max-w-sm" data-testid="calendar-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <span className="h-3 w-3 shrink-0" style={{ backgroundColor: detail?.color || TYPE_STYLES[detail?.item_type] }} />
              {detail && TYPE_LABELS[detail.item_type]}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <p className="font-heading text-lg font-semibold" data-testid="calendar-detail-title">{detail.title}</p>
              <div className="space-y-1.5 text-gray-600">
                <p><span className="font-medium text-gray-500">Tarih:</span> {dayjs(detail.date).format("D MMMM YYYY")}</p>
                {detail.time && <p><span className="font-medium text-gray-500">Saat:</span> {detail.time}</p>}
                {detail.item_type === "randevu" && detail.phone && (
                  <p><span className="font-medium text-gray-500">Telefon:</span> {detail.phone}</p>
                )}
                {detail.item_type === "randevu" && detail.address && (
                  <p><span className="font-medium text-gray-500">Adres:</span> {detail.address}</p>
                )}
                {detail.item_type === "gorev" && detail.priority && (
                  <p><span className="font-medium text-gray-500">Öncelik:</span> {PRIORITY_LABELS[detail.priority] || detail.priority}</p>
                )}
                {detail.status && (
                  <p><span className="font-medium text-gray-500">Durum:</span> {STATUS_LABELS[detail.status] || detail.status}</p>
                )}
              </div>
              {(detail.description || detail.note) && (
                <div className="border-t border-gray-100 pt-3">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Açıklama</p>
                  <p className="whitespace-pre-wrap text-gray-700" data-testid="calendar-detail-description">
                    {detail.description || detail.note}
                  </p>
                </div>
              )}
              <div className="flex justify-end">
                <Button data-testid="calendar-item-delete" variant="outline" onClick={remove}
                  className="rounded-none border-red-200 text-red-600 hover:bg-red-50">
                  <Trash2 size={15} className="mr-1" /> Sil
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
